import { useRef, useState, useLayoutEffect, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MoreHorizontal } from "lucide-react";
import { useAccess, useAccessInList } from "../../../../utils/hooks/access";
import { cn } from "../../../../lib/utils";

// "notifications" collides with the nested `notifications.title.*` translation object, so it needs its own flat key
const TRANSLATION_KEY_OVERRIDES: Record<string, string> = {
    notifications: "notification",
};

function Menu({ selected, setSelected }: any) {
    const { t } = useTranslation();
    const label = (item: string) => t(TRANSLATION_KEY_OVERRIDES[item] ?? item);

    const { hasAccess: canManageUsers } = useAccess("canManageUsers");
    const { hasAccess: canManageTimeOff } = useAccess("people.manageTimeOff");
    const { hasAccess: canViewHistory } = useAccess("people.viewHistory");
    const { hasAccess: canViewExpectedHours } = useAccess("people.expectedHours.canView");
    const { hasAccess: canViewWorkingShift } = useAccess("people.workingShift.canView");
    const { hasAccess: canViewPBJ } = useAccess("people.pbjCode.canView");
    const { hasAccess: canManageNotes } = useAccessInList(["people.manageNote", "people.manageAllNotes"]);
    const { hasAccess: canManageDocuments } = useAccess("people.manageDocuments");
    const { hasAccess: canViewNotifications } = useAccess("notificationSettings.canView");
    const { hasAccess: canViewQualifications } = useAccessInList([
        "people.changeSkill",
        "people.changeLicense",
        "people.changeCertification",
    ]);

    const config = useMemo(() => {
        return [
            { key: "job-and-pay", visible: true },
            { key: "personal", visible: true },
            { key: "qualifications", visible: canViewQualifications },
            { key: "availability", visible: canManageUsers },
            { key: "settings", visible: true },
            { key: "time-off", visible: canManageTimeOff },
            { key: "history", visible: canViewHistory },
            { key: "expected-hours", visible: canViewExpectedHours },
            { key: "working-shifts", visible: canViewWorkingShift },
            { key: "pbj", visible: canViewPBJ },
            { key: "notes", visible: canManageNotes },
            { key: "documents", visible: canManageDocuments },
            { key: "schedule", visible: true },
            { key: "notifications", visible: canViewNotifications },
        ].filter((item) => item.visible).map((item) => item.key);
    }, [
        canManageUsers,
        canManageTimeOff,
        canViewHistory,
        canViewExpectedHours,
        canViewWorkingShift,
        canViewPBJ,
        canManageNotes,
        canManageDocuments,
        canViewQualifications,
        canViewNotifications,
    ]);

    const wrapperRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLDivElement>(null);
    const [visibleCount, setVisibleCount] = useState(config.length);
    const [open, setOpen] = useState(false);
    const [order, setOrder] = useState<string[]>(config);

    // Keep order in sync with config, preserving any manual reordering
    useEffect(() => {
        setOrder((prev) => {
            const kept = prev.filter((key) => config.includes(key));
            const added = config.filter((key) => !kept.includes(key));
            return [...kept, ...added];
        });
    }, [config]);

    const compute = useCallback(() => {
        const wrapper = wrapperRef.current;
        const measurer = measureRef.current;
        if (!wrapper || !measurer) return;

        const children = Array.from(measurer.children) as HTMLElement[];
        if (!children.length) return;

        const available = wrapper.offsetWidth;
        const containerPadding = 8; // p-1 = 4px each side

        const totalNatural = children.reduce((sum, el, i) => sum + el.offsetWidth + (i > 0 ? 2 : 0), 0);
        if (totalNatural + containerPadding <= available) {
            setVisibleCount(children.length);
            return;
        }

        // Reserve ~38px for the "more" button (p-1.5 + icon 16px)
        const fitAvailable = available - containerPadding;
        let used = 0, count = 0;
        for (const el of children) {
            const w = el.offsetWidth + (count > 0 ? 2 : 0);
            if (used + w <= fitAvailable) { used += w; count++; }
            else break;
        }
        setVisibleCount(Math.max(1, count));
    }, []);

    useLayoutEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;
        compute();
        const observer = new ResizeObserver(compute);
        observer.observe(wrapper);
        return () => observer.disconnect();
    }, [compute]);

    // Recompute when permissions change and config rebuilds
    useLayoutEffect(() => { compute(); }, [config, compute]);

    useEffect(() => {
        if (!open) return;
        const handler = () => setOpen(false);
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    const visibleItems = order.slice(0, visibleCount);
    const hiddenItems = order.slice(visibleCount);
    const selectedIsHidden = hiddenItems.includes(selected);

    const handleSelectHidden = (item: string) => {
        setSelected(item);
        setOpen(false);
        setOrder((prev) => {
            const idx = prev.indexOf(item);
            if (idx < 0) return prev;
            const next = [...prev];
            next.splice(idx, 1);
            const insertAt = Math.min(visibleCount - 1, next.length);
            next.splice(insertAt, 0, item);
            return next;
        });
    };

    return (
        <div ref={wrapperRef} className="tw-w-full tw-flex tw-justify-center">
            {/* Hidden measurement layer — renders all items to get their natural widths */}
            <div
                ref={measureRef}
                className="tw-invisible tw-absolute tw-flex tw-gap-0.5 tw-pointer-events-none"
                aria-hidden="true"
            >
                {order.map((item) => (
                    <span key={item} className="tw-py-1 tw-px-3 tw-text-sm tw-whitespace-nowrap">
                        {label(item)}
                    </span>
                ))}
            </div>

            <div className="tw-flex tw-bg-primary/10 tw-p-1 tw-rounded-xl tw-items-center tw-gap-0.5">
                {visibleItems.map((item) => (
                    <span
                        key={item}
                        className={cn(
                            "tw-py-1 tw-px-3 tw-cursor-pointer tw-text-sm tw-rounded-lg tw-transition-colors tw-text-neutral-600 tw-whitespace-nowrap",
                            selected === item
                                ? "tw-bg-white tw-font-medium tw-text-neutral-900 tw-border"
                                : "hover:tw-bg-primary/10"
                        )}
                        onClick={() => setSelected(item)}
                    >
                        {label(item)}
                    </span>
                ))}

                {hiddenItems.length > 0 && (
                    <div className="tw-relative">
                        <button
                            type="button"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={() => setOpen((prev) => !prev)}
                            className={cn(
                                "tw-p-1.5 tw-rounded-lg tw-transition-colors tw-flex tw-items-center tw-text-neutral-600",
                                selectedIsHidden
                                    ? "tw-bg-white tw-text-neutral-900 tw-border"
                                    : "hover:tw-bg-primary/10"
                            )}
                        >
                            <MoreHorizontal className="tw-h-4 tw-w-4" />
                        </button>

                        {open && (
                            <div
                                className="tw-absolute tw-right-0 tw-top-full tw-mt-1 tw-bg-white tw-border tw-rounded-xl tw-z-50 tw-min-w-[160px] tw-py-1 tw-overflow-hidden"
                                onMouseDown={(e) => e.stopPropagation()}
                            >
                                {hiddenItems.map((item) => (
                                    <span
                                        key={item}
                                        className={cn(
                                            "tw-block tw-px-3 tw-py-2 tw-text-sm tw-cursor-pointer tw-transition-colors tw-whitespace-nowrap",
                                            selected === item
                                                ? "tw-font-medium tw-text-primary tw-bg-primary/5"
                                                : "tw-text-neutral-600 hover:tw-bg-muted/50"
                                        )}
                                        onClick={() => handleSelectHidden(item)}
                                    >
                                        {label(item)}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default Menu;
